import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock, Loader2, CheckCircle2, Sparkles, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  const verifyToken = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (e) {
      console.warn('Token verify:', e.message);
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token, verifyToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error(language === 'fr' ? 'Remplis tous les champs' : 'Fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit faire au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(language === 'fr' ? 'Mot de passe mis à jour !' : 'Password updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{language === 'fr' ? 'Vérification...' : 'Verifying...'}</span>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-stone-100 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              {language === 'fr' ? 'Lien invalide ou expiré' : 'Invalid or expired link'}
            </h1>
            <p className="text-stone-500 mb-6">
              {language === 'fr' 
                ? 'Ce lien de réinitialisation n\'est plus valide. Demande un nouveau lien.'
                : 'This reset link is no longer valid. Request a new one.'}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="btn-primary w-full"
              >
                {language === 'fr' ? 'Nouveau lien' : 'Request new link'}
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-stone-100 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              {language === 'fr' ? 'Mot de passe mis à jour !' : 'Password updated!'}
            </h1>
            <p className="text-stone-500 mb-6">
              {language === 'fr' 
                ? 'Tu peux maintenant te connecter avec ton nouveau mot de passe.'
                : 'You can now sign in with your new password.'}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="btn-primary w-full"
              data-testid="go-to-login-btn"
            >
              {language === 'fr' ? 'Se connecter' : 'Sign in'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Back button */}
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
        </button>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-stone-100">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-stone-900">
              {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
            </h1>
            <p className="text-stone-500 mt-2 text-sm">
              {language === 'fr' 
                ? `Crée un nouveau mot de passe pour ${email}`
                : `Create a new password for ${email}`}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 pr-12 h-12 rounded-xl border-stone-200 focus:border-[#FF6B6B] focus:ring-[#FF6B6B]"
                  data-testid="reset-password-new"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 h-12 rounded-xl border-stone-200 focus:border-[#FF6B6B] focus:ring-[#FF6B6B]"
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-stone-50 rounded-xl p-3">
              <p className="text-xs text-stone-500">
                {language === 'fr' 
                  ? '• Minimum 6 caractères'
                  : '• Minimum 6 characters'}
              </p>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full h-12"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {language === 'fr' ? 'Mise à jour...' : 'Updating...'}
                </>
              ) : (
                language === 'fr' ? 'Mettre à jour le mot de passe' : 'Update password'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
